import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, IconButton, InputBase, Chip, makeStyles } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import TuneIcon from "@material-ui/icons/Tune";
import CloseIcon from "@material-ui/icons/Close";
import PlaceIcon from "@material-ui/icons/Place";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { DateTime } from "luxon";
import { Vehicle, VehicleStatus } from "../types";
import { statusColor } from "../constants";
import { DrivingCarIcon, ParkedCarIcon } from "./CarIcons";
import { SignalStrengthIcon } from "./SignalStrengthIcon";

const STATUS_FILTERS: Array<VehicleStatus | "all"> = ["all", "online", "offline"];

// lastActive is a UNIX-seconds timestamp of the device's last MQTT traffic (see deviceStates).
function formatLastActive(lastActive: number | null): string {
  if (lastActive == null) return "Never reported";
  return DateTime.fromSeconds(lastActive).toRelative() ?? "Unknown";
}

const useStyles = makeStyles(() => ({
  root: { display: "flex", flexDirection: "column", height: "100%", width: 272, backgroundColor: "#fff", borderRight: "1px solid #e5e7eb" },
  header: { padding: "12px 12px 8px 12px", borderBottom: "1px solid #f3f4f6" },
  headerTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerLeft: { display: "flex", alignItems: "center", gap: 8 },
  title: { fontSize: 14, fontWeight: 600, color: "#1f2937" },
  totalChip: { height: 20, fontSize: 11, backgroundColor: "#f3f4f6", color: "#6b7280" },
  liveChip: { height: 20, fontSize: 11, backgroundColor: "#dcfce7", color: "#15803d", fontWeight: 600 },
  headerActions: { display: "flex", gap: 2 },
  iconBtn: { color: "#9ca3af" },
  iconBtnActive: { color: "var(--brand-accent)", backgroundColor: "color-mix(in srgb, var(--brand-accent) 8%, white)" },
  searchRow: { marginTop: 8, display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", borderRadius: 8, border: "1px solid #e5e7eb", backgroundColor: "#f9fafb" },
  searchIcon: { fontSize: 16, color: "#9ca3af" },
  searchInput: { fontSize: 13, flex: 1 },
  filterRow: { marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 },
  filterChip: { height: 24, fontSize: 12, backgroundColor: "transparent", color: "#6b7280", border: "1px solid #e5e7eb", fontWeight: 400 },
  filterChipActive: { backgroundColor: "color-mix(in srgb, var(--brand-accent) 8%, white)", color: "var(--brand-accent)", border: "1px solid color-mix(in srgb, var(--brand-accent) 40%, white)", fontWeight: 600 },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  emptyMessage: { fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 16 },
  card: {
    textAlign: "left",
    borderRadius: 12,
    border: "1px solid #f3f4f6",
    backgroundColor: "#fff",
    padding: 12,
    cursor: "pointer",
    boxShadow: "none",
    "&:hover": { borderColor: "color-mix(in srgb, var(--brand-accent) 25%, white)", backgroundColor: "#f9fafb" },
  },
  cardSelected: {
    border: "1px solid color-mix(in srgb, var(--brand-accent) 40%, white)",
    backgroundColor: "color-mix(in srgb, var(--brand-accent) 8%, white)",
    boxShadow: "0px 1px 3px 0px rgba(0,0,0,0.15)",
    "&:hover": { backgroundColor: "color-mix(in srgb, var(--brand-accent) 8%, white)" },
  },
  cardTopRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  cardTopLeft: { display: "flex", alignItems: "center", gap: 6 },
  statusDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  plate: { fontWeight: 700, fontSize: 14, color: "#111827" },
  cardTopRight: { display: "flex", alignItems: "center", gap: 6 },
  makeModelRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#9ca3af", marginBottom: 8 },
  typeDivider: { width: 1, height: 12, backgroundColor: "#e5e7eb" },
  typeText: { textTransform: "capitalize", color: "#6b7280" },
  locationRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" },
  locationIcon: { fontSize: 12, color: "#9ca3af" },
  locationText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  lastActiveRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#9ca3af", marginTop: 4 },
  lastActiveIcon: { fontSize: 12, color: "#9ca3af" },
}));

interface VehiclePanelProps {
  vehicles: Vehicle[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  // totalCount/onlineCount are just vehicles.length/online count here — a business customer's
  // active-booking vehicle list is always small (see useTrackedVehicles), fetched in one shot,
  // no server-side pagination to track separately.
  totalCount: number;
  onlineCount: number;
}

export function VehiclePanel({ vehicles, selectedId, onSelect, onClose, totalCount, onlineCount }: VehiclePanelProps) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<VehicleStatus | "all">("all");
  const [showFilter, setShowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // `vehicles` (from useTrackedVehicles) is already online-first and re-sorts live as
  // deviceStates polls in, so filtering here doesn't need to re-sort itself.
  const filtered = useMemo(
    () =>
      vehicles.filter((v) => {
        const q = search.toLowerCase();
        const matchSearch =
          v.plate.toLowerCase().includes(q) ||
          v.make.toLowerCase().includes(q) ||
          v.model.toLowerCase().includes(q) ||
          v.vin.toLowerCase().includes(q);
        return matchSearch && (filterStatus === "all" || v.status === filterStatus);
      }),
    [vehicles, search, filterStatus]
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [search, filterStatus]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Box className={classes.headerLeft}>
            <Typography className={classes.title}>Vehicles</Typography>
            <Chip label={`${totalCount} total`} size="small" className={classes.totalChip} />
            <Chip label={`${onlineCount} live`} size="small" className={classes.liveChip} />
          </Box>
          <Box className={classes.headerActions}>
            <IconButton
              size="small"
              onClick={() => { setShowFilter((p) => !p); setShowSearch(false); }}
              className={filterStatus !== "all" ? classes.iconBtnActive : classes.iconBtn}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => { setShowSearch((p) => !p); setShowFilter(false); }}
              className={showSearch || search ? classes.iconBtnActive : classes.iconBtn}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose} className={classes.iconBtn}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {showSearch && (
          <Box className={classes.searchRow}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              autoFocus
              placeholder="Search plate, make, model, VIN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={classes.searchInput}
            />
          </Box>
        )}

        {showFilter && (
          <Box className={classes.filterRow}>
            {STATUS_FILTERS.map((s) => (
              <Chip
                key={s}
                label={s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                size="small"
                onClick={() => { setFilterStatus(s); setShowFilter(false); }}
                className={filterStatus === s ? classes.filterChipActive : classes.filterChip}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box ref={scrollRef} className={classes.list}>
        {filtered.length === 0 && (
          <Typography className={classes.emptyMessage}>No vehicles match.</Typography>
        )}
        {filtered.map((v) => {
          const isSelected = selectedId === v.id;
          // speedVal is the last cached telematicCoreData.speed and isn't reset when the
          // device drops connection — without the status check, a vehicle that went offline
          // mid-drive shows a stale speed forever with the "driving" icon animating.
          const isDriving = v.status === "online" && v.speedVal > 0;
          return (
            <Box
              key={v.id}
              component="button"
              onClick={() => onSelect(v.id === selectedId ? "" : v.id)}
              className={`${classes.card} ${isSelected ? classes.cardSelected : ""}`}
            >
              <Box className={classes.cardTopRow}>
                <Box className={classes.cardTopLeft}>
                  <Box title={v.status === "online" ? "Online" : "Offline"} className={classes.statusDot} style={{ backgroundColor: statusColor[v.status] }} />
                  <Typography className={classes.plate}>{v.plate}</Typography>
                </Box>
                <Box className={classes.cardTopRight}>
                  {isDriving ? <DrivingCarIcon /> : <ParkedCarIcon />}
                  <Box title={v.signalLevel != null ? `Signal ${v.signalLevel}%` : "No signal data"}>
                    <SignalStrengthIcon level={v.signalLevel} />
                  </Box>
                </Box>
              </Box>
              <Box className={classes.makeModelRow}>
                <span>{v.make} {v.model}</span>
                {v.type && (
                  <>
                    <Box component="span" className={classes.typeDivider} />
                    <Box component="span" className={classes.typeText}>{v.type}</Box>
                  </>
                )}
              </Box>
              <Box className={classes.locationRow}>
                <PlaceIcon className={classes.locationIcon} />
                <Box component="span" className={classes.locationText}>{v.location}</Box>
              </Box>
              <Box className={classes.lastActiveRow}>
                <AccessTimeIcon className={classes.lastActiveIcon} />
                <Box component="span">Last active {formatLastActive(v.lastActive)}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
